<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { IconAdd, IconDelete } from '@/components/design/icons';
import {
  ActionTriggerMode,
  PlaybookRiskLevel,
  type GroupTriggerInDb,
  type PlaybookEvaluationGroupInDb,
} from '@/api';
import { playbookRiskLevelMap } from '@/components/design/statusMaps';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import { formatGroupTriggerRule } from '@/services/playbooks/playbookRunDisplay';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaEnumSelect from '@/components/design/base/selects/GaiaEnumSelect.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

type View = 'list' | 'form';
type FormMode = 'create' | 'edit';

const props = withDefaults(
  defineProps<{
    show: boolean;
    group?: PlaybookEvaluationGroupInDb | null;
  }>(),
  {
    group: null,
  }
);

const emit = defineEmits<{
  (event: 'update:show', value: boolean): void;
  (event: 'saved'): void;
}>();

const message = useGaiaToast();
const saving = ref(false);
const loadingTriggers = ref(false);
const deletingAll = ref(false);
const deletingTriggerUids = reactive<Record<string, boolean>>({});
const existingTriggers = ref<GroupTriggerInDb[]>([]);
const view = ref<View>('form');
const formMode = ref<FormMode>('create');
const editingTriggerUid = ref<string | null>(null);

const form = reactive<{
  mode: ActionTriggerMode;
  minRiskLevel: PlaybookRiskLevel;
  minCount: number;
}>({
  mode: ActionTriggerMode.AT_LEAST,
  minRiskLevel: PlaybookRiskLevel.MEDIUM,
  minCount: 1,
});

const hasMembers = computed(() => (props.group?.evaluation_uids?.length ?? 0) > 0);

const modeOptions = computed(() => [
  { label: 'At least N results', value: ActionTriggerMode.AT_LEAST, disabled: !hasMembers.value },
  { label: 'Fewer than N results', value: ActionTriggerMode.LESS_THAN },
  { label: 'No results', value: ActionTriggerMode.NONE_FOUND },
  { label: 'Always', value: ActionTriggerMode.ALWAYS },
]);
const showThresholdFields = computed(
  () => form.mode === ActionTriggerMode.AT_LEAST || form.mode === ActionTriggerMode.LESS_THAN
);
const showRiskLevelField = computed(() => form.mode !== ActionTriggerMode.ALWAYS);

watch(
  () => props.show,
  show => {
    if (show) void openModal();
  },
  { immediate: true }
);

async function openModal(): Promise<void> {
  resetForm();
  if (!props.group) return;
  loadingTriggers.value = true;
  try {
    existingTriggers.value = await PlaybooksService.listGroupTriggers(props.group.uid);
  } catch (error) {
    existingTriggers.value = [];
    message.error(extractErrorDetail(error, 'Failed to load group triggers.'));
  } finally {
    loadingTriggers.value = false;
  }
  // Jump straight to the create form when there is nothing to manage yet.
  view.value = existingTriggers.value.length > 0 ? 'list' : 'form';
}

function resetForm(): void {
  form.mode = hasMembers.value ? ActionTriggerMode.AT_LEAST : ActionTriggerMode.ALWAYS;
  form.minRiskLevel = PlaybookRiskLevel.MEDIUM;
  form.minCount = 1;
  formMode.value = 'create';
  editingTriggerUid.value = null;
}

function startCreate(): void {
  resetForm();
  view.value = 'form';
}

function startEdit(trigger: GroupTriggerInDb): void {
  form.mode = trigger.mode;
  form.minRiskLevel = trigger.min_risk_level;
  form.minCount = trigger.min_count;
  formMode.value = 'edit';
  editingTriggerUid.value = trigger.uid;
  view.value = 'form';
}

const canSave = computed(() => {
  if (!props.group) return false;
  if (form.mode === ActionTriggerMode.AT_LEAST || form.mode === ActionTriggerMode.LESS_THAN) {
    return form.minCount >= 1;
  }
  return true;
});

function close(): void {
  emit('update:show', false);
}

async function save(): Promise<void> {
  if (!props.group || !canSave.value || saving.value) return;
  saving.value = true;
  const payload = {
    mode: form.mode,
    ...(showRiskLevelField.value ? { min_risk_level: form.minRiskLevel } : {}),
    ...(showThresholdFields.value ? { min_count: form.minCount } : {}),
  };
  try {
    if (formMode.value === 'edit' && editingTriggerUid.value) {
      await PlaybooksService.updateGroupTrigger(editingTriggerUid.value, payload);
      message.success('Group trigger updated.');
    } else {
      await PlaybooksService.createGroupTrigger(props.group.uid, payload);
      message.success('Group trigger created.');
    }
    emit('saved');
    // Refresh the list so the just-saved trigger is reflected, then return to the overview.
    existingTriggers.value = await PlaybooksService.listGroupTriggers(props.group.uid);
    resetForm();
    view.value = 'list';
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to save group trigger.'));
  } finally {
    saving.value = false;
  }
}

async function deleteTrigger(trigger: GroupTriggerInDb): Promise<void> {
  if (!props.group) return;
  deletingTriggerUids[trigger.uid] = true;
  try {
    await PlaybooksService.deleteTrigger(trigger.uid);
    message.success('Trigger deleted.');
    emit('saved');
    existingTriggers.value = await PlaybooksService.listGroupTriggers(props.group.uid);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to delete trigger.'));
  } finally {
    delete deletingTriggerUids[trigger.uid];
  }
}

async function deleteAllTriggers(): Promise<void> {
  if (!props.group || deletingAll.value) return;
  deletingAll.value = true;
  try {
    await PlaybooksService.deleteGroupTriggers(props.group.uid);
    message.success('All triggers deleted.');
    emit('saved');
    existingTriggers.value = await PlaybooksService.listGroupTriggers(props.group.uid);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to delete triggers.'));
  } finally {
    deletingAll.value = false;
  }
}
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    title="Group triggers"
    style="width: min(560px, calc(100vw - 32px))"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <n-alert v-if="!hasMembers" type="info" style="margin-bottom: 12px">
      This group has no evaluations yet — only trigger modes that don't require members (such as
      "Always") are available.
    </n-alert>

    <n-spin :show="loadingTriggers">
      <!-- Overview of existing triggers -->
      <div v-if="view === 'list'" class="group-trigger__list">
        <div
          v-for="trigger in existingTriggers"
          :key="trigger.uid"
          class="group-trigger__list-item"
        >
          <span class="group-trigger__list-desc">{{ formatGroupTriggerRule(trigger) }}</span>
          <div class="group-trigger__list-actions">
            <n-button size="small" secondary @click="startEdit(trigger)">Edit</n-button>
            <n-popconfirm @positive-click="deleteTrigger(trigger)">
              <template #trigger>
                <n-button
                  size="small"
                  quaternary
                  circle
                  type="error"
                  aria-label="Delete trigger"
                  :loading="deletingTriggerUids[trigger.uid]"
                >
                  <template #icon>
                    <n-icon><IconDelete /></n-icon>
                  </template>
                </n-button>
              </template>
              Delete this trigger?
            </n-popconfirm>
          </div>
        </div>

        <n-button v-if="existingTriggers.length === 0" dashed block @click="startCreate">
          <template #icon>
            <n-icon><IconAdd /></n-icon>
          </template>
          Add trigger
        </n-button>

        <n-popconfirm v-if="existingTriggers.length > 1" @positive-click="deleteAllTriggers">
          <template #trigger>
            <n-button text type="error" :loading="deletingAll">Delete all triggers</n-button>
          </template>
          Delete all triggers for this group?
        </n-popconfirm>
      </div>

      <!-- Create / edit form -->
      <n-form v-else label-placement="top">
        <n-form-item label="Fire when" required>
          <gaia-select v-model:value="form.mode" :options="modeOptions" />
        </n-form-item>
        <n-form-item v-if="showRiskLevelField" label="Minimum risk level" required>
          <gaia-enum-select
            v-model:value="form.minRiskLevel"
            :enum-source="PlaybookRiskLevel"
            :labels="playbookRiskLevelMap"
          />
        </n-form-item>
        <n-form-item v-if="showThresholdFields" label="Minimum count" required>
          <gaia-input-number v-model:value="form.minCount" :min="1" style="width: 100%" />
        </n-form-item>
      </n-form>
    </n-spin>

    <template #footer>
      <n-space justify="end">
        <n-button v-if="view === 'form' && existingTriggers.length > 0" @click="view = 'list'">
          Back
        </n-button>
        <n-button @click="close">Cancel</n-button>
        <n-button
          v-if="view === 'form'"
          type="primary"
          :loading="saving"
          :disabled="!canSave"
          @click="save"
        >
          {{ formMode === 'edit' ? 'Save' : 'Create' }}
        </n-button>
      </n-space>
    </template>
  </n-modal>
</template>

<style scoped>
.group-trigger__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.group-trigger__list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--n-border-color, rgba(0, 0, 0, 0.12));
  border-radius: 8px;
}

.group-trigger__list-desc {
  min-width: 0;
  font-size: 14px;
}

.group-trigger__list-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
</style>
